import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaBars, FaTimes, FaPhone, FaWhatsapp
} from 'react-icons/fa';
import logo from '@assets/micropoint-logo.jpg'; 

const navItems = [
  { path: '/', label: 'Home' },
  // { path: '/solutions', label: 'Solutions' },
  { path: '/about', label: 'About' },
  { path: '/products', label: 'Products' },
  { path: '/case-studies', label: 'Case Studies' }, // ADDED
  { path: '/resources', label: 'Resources' },
  { path: '/demo', label: 'Demo' },
  { path: 'https://micropointintern.lovable.app/', label: 'Careers', external: true },
  { path: '/contact', label: 'Contact' },
];

const topNavItems = [
  { path: '/clients', label: 'Clients' },
  { path: '/partners', label: 'Partners' },
  { path: '/faqs', label: 'FAQs' },
  { path: '/security', label: 'Security' },
];

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const location = useLocation();

  // Sticky header on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('menu-open');
    } else {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('menu-open');
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.classList.remove('menu-open');
    };
  }, [isMenuOpen]);

  return (
    <>
      {/* Top Bar - Hidden on mobile */}
      <div className="bg-white border-b border-gray-200 px-4 py-1.5 hidden md:flex items-center justify-between text-sm">
        <div className="flex items-center gap-6">
          {topNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `top-nav-link ${isActive ? 'active' : ''}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-4">
          <a 
            href="tel:+254703802020" 
            className="text-gray-700 hover:text-micropoint-gold transition-colors flex items-center gap-1.5 text-sm"
          >
            <FaPhone className="text-micropoint-gold text-sm" />
            +254 703 802 020
          </a>
          <a 
            href="https://api.whatsapp.com/send?phone=254703802020" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-green-500 hover:text-green-600 transition-all hover:scale-110"
          >
            <FaWhatsapp className="text-xl" />
          </a>
          <Link 
            to="/demo"
            className="bg-gradient-to-r from-micropoint-gold to-yellow-500 hover:from-yellow-500 hover:to-micropoint-gold text-white px-4 py-1.5 rounded-full font-semibold transition-all hover:shadow-lg hover:scale-105 text-sm"
          >
            Request Demo
          </Link>
        </div>
      </div>

      {/* Main Navbar */}
      <header 
        className={`bg-white border-b border-gray-200 transition-all duration-300 ${
          isSticky ? 'fixed top-0 left-0 right-0 z-50 shadow-lg' : ''
        }`}
      >
        <div className="container-custom py-2 md:py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center hover:scale-105 transition-transform duration-300 flex-shrink-0"
            >
              <img 
                src={logo} 
                alt="Micropoint Systems" 
                className="h-10 md:h-12 w-auto rounded-lg object-contain" 
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => {
                if (item.external) {
                  return (
                    <a
                      key={item.path}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="nav-main-bounce px-3 py-2 rounded-lg text-sm font-medium"
                    >
                      {item.label}
                    </a>
                  );
                }
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `nav-main-bounce ${isActive ? 'active' : ''} px-3 py-2 rounded-lg text-sm font-medium`
                    }
                  >
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-2 lg:hidden">
              <a 
                href="tel:+254703802020" 
                className="text-gray-600 hover:text-micropoint-gold transition-colors p-2"
                aria-label="Call us"
              >
                <FaPhone className="text-lg" />
              </a>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-600 hover:text-micropoint-gold text-2xl p-2 transition-all hover:scale-110"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setIsMenuOpen(false)}
            />
            
            {/* Slide-in Menu */}
            <motion.div
              initial={{ x: '100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0 }}
              transition={{ type: 'tween', duration: 0.3 }}
              className="fixed top-0 right-0 h-full w-[280px] sm:w-[320px] bg-white shadow-2xl z-50 lg:hidden overflow-y-auto"
            >
              <div className="p-5 sm:p-6">
                {/* Close button - Always visible at top */}
                <div className="flex justify-between items-center mb-6">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Menu</span>
                  <button
                    onClick={() => setIsMenuOpen(false)}
                    className="text-gray-600 hover:text-micropoint-gold bg-gray-100 hover:bg-micropoint-gold/10 p-3 rounded-full transition-all duration-300 hover:scale-110"
                    aria-label="Close menu"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>
                
                {/* Mobile Logo */}
                <div className="flex items-center justify-center mb-6 pb-4 border-b border-gray-200">
                  <img 
                    src={logo} 
                    alt="Micropoint Systems" 
                    className="h-12 w-auto rounded-lg object-contain" 
                  />
                </div>
                
                {/* Mobile Nav Items - No horizontal scroll */}
                <nav className="flex flex-col space-y-1">
                  {navItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    
                    if (item.external) {
                      return (
                        <a
                          key={item.path}
                          href={item.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsMenuOpen(false)}
                          className="block w-full px-4 py-3 rounded-lg transition-all text-left font-medium text-gray-600 hover:text-micropoint-gold hover:bg-gray-50"
                        >
                          {item.label}
                          <span className="ml-2 text-xs text-gray-400">↗</span>
                        </a>
                      );
                    }
                    
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive: active }) =>
                          `block w-full px-4 py-3 rounded-lg transition-all text-left font-medium ${
                            active
                              ? 'bg-gradient-to-r from-micropoint-gold/10 to-yellow-500/10 text-micropoint-gold'
                              : 'text-gray-600 hover:text-micropoint-gold hover:bg-gray-50'
                          }`
                        }
                      >
                        {item.label}
                        {isActive && (
                          <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-micropoint-gold" />
                        )}
                      </NavLink>
                    );
                  })}
                </nav>
                
                {/* Mobile Top Nav Items */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Company</p>
                  <div className="grid grid-cols-2 gap-1">
                    {topNavItems.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsMenuOpen(false)}
                        className={({ isActive }) =>
                          `block px-3 py-2 rounded-lg text-center text-sm transition-all ${
                            isActive
                              ? 'bg-micropoint-gold/10 text-micropoint-gold'
                              : 'text-gray-500 hover:text-micropoint-gold hover:bg-gray-50'
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
                
                {/* Mobile Actions */}
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                  <Link
                    to="/demo"
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full bg-gradient-to-r from-micropoint-gold to-yellow-500 hover:from-yellow-500 hover:to-micropoint-gold text-white px-6 py-3 rounded-lg font-semibold text-center transition-all hover:scale-[1.02] text-sm"
                  >
                    Request Demo
                  </Link>
                  
                  {/* Quick contact in mobile menu */}
                  <div className="text-center text-sm text-gray-500 space-y-2">
                    <a href="tel:+254703802020" className="hover:text-micropoint-gold transition-colors block py-1 flex items-center justify-center gap-2">
                      <FaPhone className="text-micropoint-gold text-sm" /> +254 703 802 020
                    </a>
                    <a href="https://api.whatsapp.com/send?phone=254703802020" target="_blank" rel="noopener noreferrer" className="hover:text-micropoint-gold transition-colors block py-1 flex items-center justify-center gap-2">
                      <FaWhatsapp className="text-green-500 text-sm" /> WhatsApp: +254 730 320 320
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spacer for fixed header */}
      {isSticky && <div className="h-[56px] md:h-[68px]" />}
    </>
  );
}